import React, { useState, useMemo, useEffect } from 'react'
import BaseBottomSheetPopup from '../../../../components/BasicComponent/BaseBottomSheetPopup'
import BaseButton from '../../../../components/BasicComponent/BaseButton'
import FixedBottom from '../../../../components/BasicComponent/FixedBottom'
import LoadingScreen from '../../../../components/BasicComponent/LoadingScreen'
import { IconCheckCircleGreen, IconArrowRightFull } from '../../../../assets/icons'
import { VEHICLE_TYPE } from '../../../../constants/global'
import { formatTwoDigitValue, number_to_price } from '../../../../helper/common'
import './index.scss'

const TABS = [
  { id: VEHICLE_TYPE.CAR, label: 'Xe ô tô' },
  { id: VEHICLE_TYPE.MOTOBIKE, label: 'Xe máy' },
  { id: VEHICLE_TYPE.ELECTRIC_MOTORBIKE, label: 'Xe máy điện' }
]

export default function Layout2({
  products,
  selectedProductId,
  setSelectedProductId,
  isLoadingProducts,
  hasLoadError,
  handleRetryLoadProducts,
  handleNextStep,
  getProductBenefitItems,
  getBenefits,
  getProductPriceText,
  getVehicleCountText,
  getProductVehicleType,
  isMotorbikeProduct
}) {
  const [activeTab, setActiveTab] = useState(TABS[0].id)
  const [detailPopupProduct, setDetailPopupProduct] = useState(null)

  useEffect(() => {
    if (selectedProductId && products.length > 0) {
      const selectedProduct = products.find(p => Number(p.productId) === Number(selectedProductId))
      if (selectedProduct) {
        setActiveTab(getProductVehicleType(selectedProduct.vehicleType))
      }
    }
  }, [selectedProductId, products, getProductVehicleType])

  // Calculate counts for each tab
  const tabCounts = useMemo(() => {
    const counts = {
      [VEHICLE_TYPE.CAR]: 0,
      [VEHICLE_TYPE.MOTOBIKE]: 0,
      [VEHICLE_TYPE.ELECTRIC_MOTORBIKE]: 0
    }
    products.forEach(p => {
      const type = getProductVehicleType(p.vehicleType)
      if (counts[type] !== undefined) {
        counts[type] += 1
      }
    })
    return counts
  }, [products, getProductVehicleType])

  // Get products for active tab
  const currentProducts = useMemo(() => {
    return products.filter(p => getProductVehicleType(p.vehicleType) === activeTab)
  }, [products, activeTab, getProductVehicleType])

  const handleOpenDetail = (product) => {
    setDetailPopupProduct(product)
  }

  const handleCloseDetail = () => {
    setDetailPopupProduct(null)
  }

  const handleSelectProduct = (product) => {
    setSelectedProductId(product.productId)
    if (detailPopupProduct) {
      handleCloseDetail()
    }
  }

  return (
    <>
      <div className="TrafficFineLayout2">
        {hasLoadError && !isLoadingProducts ? (
          <div className="TrafficFineLayout2_empty">{'Đã xảy ra lỗi, vui lòng thử lại sau'}</div>
        ) : (
          <div className="TrafficFineLayout2_content">
            <div className="TrafficFineLayout2_tabs">
              {TABS.map(tab => {
                const isActive = activeTab === tab.id
                return (
                  <button
                    key={tab.id}
                    className={`TrafficFineLayout2_tab ${isActive ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label} <span className="badge">{tabCounts[tab.id]}</span>
                  </button>
                )
              })}
            </div>

            <div className="TrafficFineLayout2_list">
              {!isLoadingProducts && currentProducts.length > 0 ? (
                currentProducts.map((product) => {
                  const isSelected = Number(product.productId) === Number(selectedProductId)
                  const priceText = getProductPriceText(product)
                  const [pricePart, unitPart] = priceText.split('đ')
                  const formattedPrice = `${pricePart}đ${unitPart || ''}`
                  
                  return (
                    <div key={product.productId} className={`TrafficFineLayout2_card ${isSelected ? 'selected' : ''}`}>
                      <div className="card-title">{product.productName}</div>
                      
                      <div className="card-row">
                        <span className="label">Số lượng xe</span>
                        <span className="value">{getVehicleCountText(product)}</span>
                      </div>
                      
                      <div className="card-row">
                        <span className="label">Giá gói/ tháng</span>
                        <span className="value">{formattedPrice}</span>
                      </div>

                      <div className="card-actions">
                        <button 
                          type="button" 
                          className="btn-detail"
                          onClick={() => handleOpenDetail(product)}
                        >
                          <span>Chi tiết</span> <IconArrowRightFull />
                        </button>
                        <button 
                          type="button" 
                          className={`btn-select ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleSelectProduct(product)}
                        >
                          {isSelected ? 'Đã chọn' : 'Chọn'}
                        </button>
                      </div>
                    </div>
                  )
                })
              ) : !isLoadingProducts ? (
                <div className="TrafficFineLayout2_empty">{'Chưa có gói dịch vụ phù hợp.'}</div>
              ) : null}
            </div>
          </div>
        )}
      </div>

      <FixedBottom elementPaddingBottom="LayoutPartner">
        <div className="TrafficFineLayout2_bottom">
          {hasLoadError && !isLoadingProducts ? (
            <BaseButton onClick={handleRetryLoadProducts}>Thử lại</BaseButton>
          ) : (
            <BaseButton disabled={!selectedProductId} onClick={handleNextStep}>
              Tiếp theo
            </BaseButton>
          )}
        </div>
      </FixedBottom>
      
      <LoadingScreen open={isLoadingProducts} />

      {/* Detail Bottom Sheet */}
      <BaseBottomSheetPopup
        open={!!detailPopupProduct}
        onClose={handleCloseDetail}
        title={detailPopupProduct?.productName || 'Chi tiết gói'}
        className="TrafficFineLayout2_detailPopup"
      >
        {detailPopupProduct && (
          <div className="detail-popup-content">
            <div className="detail-row">
              <span className="label">Giá gói/ tháng</span>
              <div className="value-group">
                <span className="value">{getProductPriceText(detailPopupProduct)}</span>
              </div>
            </div>

            <div className="detail-row">
              <span className="label">Số lượng xe</span>
              <span className="value">{getVehicleCountText(detailPopupProduct)}</span>
            </div>

            <div className="benefits-list">
              {getProductBenefitItems(getBenefits(detailPopupProduct?.productOptionalInfo)).map(item => (
                <div key={item.key} className="benefit-item">
                  <span className="benefit-label">{item.label}</span>
                  {item.isActive ? (
                    <IconCheckCircleGreen className="benefit-status-icon" />
                  ) : (
                    <span className="benefit-status-placeholder" />
                  )}
                </div>
              ))}
            </div>

            <div className="popup-actions">
              <BaseButton onClick={() => handleSelectProduct(detailPopupProduct)}>
                Chọn
              </BaseButton>
            </div>
          </div>
        )}
      </BaseBottomSheetPopup>
    </>
  )
}
